import React, { useEffect, useState } from 'react'
import { Formik, FormikHelpers, FormikTouched } from 'formik'
import { AnySchema } from 'yup'
import { partyApiConstants, partyApiSelectors } from 'modules/api/party'
import { Button, FormLoader, Heading, InfoBox, Tooltip } from '../../..'
import classNames from 'classnames'
import BankDetails from '../../FormGroups/BankDetails'
import ButtonContainer from './ButtonContainer'
import EntityDetails from '../../FormGroups/EntityDetails'
import styles from './PartyFormHelper.module.scss'
import { isEmpty, mergeRight } from 'ramda'
import FormErrors from '../../../molecules/FormErrors/FormErrors'
import { useSelector } from 'react-redux'
import AddressFormGroup from '../../FormGroups/AddressFormGroup'
import { hasAllRequiredFields } from 'utils/yup'
import Info from 'components/atoms/Svgs/Info'
import { useHover } from 'react-use'

type PartyEntityAddressValues = {
  addressLine1: string
  addressLine2: string
  city: string
  province: string
  postalCode: string
  country: string
}

const emptyAddress: PartyEntityAddressValues = {
  addressLine1: '',
  addressLine2: '',
  city: '',
  province: '',
  postalCode: '',
  country: '',
}

const hasAddressValues = (address?: Partial<PartyEntityAddressValues>): boolean =>
  Object.values(address ?? {}).some(value => Boolean(String(value ?? '').trim()))

type PartyEntityFormValues = {
  tags: string[]
  companyName?: string
  tradingAs?: string
  companyRegistration?: string | null
  vatNumber?: string
  emailAddress?: string
  alternativeEmailAddress?: string
  cellNumber?: string
  telNumber?: string
  contactPersonFirstName?: string
  contactPersonLastName?: string
  bank: string
  accountType: string
  accountNumber: string
  accountName?: string
  address?: PartyEntityAddressValues
}

type FieldErrors = Partial<Record<keyof PartyEntityFormValues, string>>

interface PartyEntityFormProps {
  validationSchema?: AnySchema
  requiredFields: string[]
  initialValues: PartyEntityFormValues
  onSubmit: (
    values: PartyEntityFormValues & { partyType: number },
    resetForm: FormikHelpers<PartyEntityFormValues>['resetForm'],
  ) => void
  isSubmitting?: boolean
  getGeneralErrors: (values: PartyEntityFormValues) => string[]
  getFieldErrors: (values: PartyEntityFormValues) => FieldErrors
  allowSearchable?: boolean
  getButtonText?: (values: PartyEntityFormValues) => string
  isDisabled?: boolean
  isEditingContact?: boolean
}

const PartyEntityForm = ({
  validationSchema,
  requiredFields,
  onSubmit,
  initialValues,
  isSubmitting,
  getGeneralErrors,
  getFieldErrors,
  allowSearchable,
  getButtonText,
  isDisabled,
  isEditingContact,
}: PartyEntityFormProps) => {
  const [selectedPartyId, setSelectedPartyId] = useState<string | null>(null)
  const [showAddress, setShowAddress] = useState(hasAddressValues(initialValues?.address))

  // Expand the address section automatically when an existing contact already has an address
  // (initial values may load asynchronously after first render).
  useEffect(() => {
    if (hasAddressValues(initialValues?.address)) {
      setShowAddress(true)
    }
  }, [initialValues?.address])

  const [hoverableInfoIcon, isHovering] = useHover(
    <div className={styles['info-icon']}>
      <Info width={20} height={20} />
    </div>,
  )

  const getPartyValues = useSelector(partyApiSelectors.getPartyDetailsById)

  return (
    <Formik
      validateOnBlur
      validateOnChange
      enableReinitialize
      validationSchema={validationSchema}
      initialValues={initialValues}
      initialTouched={initialValues as unknown as FormikTouched<PartyEntityFormValues>}
      onSubmit={(values, { resetForm }) => {
        onSubmit({ ...values, partyType: partyApiConstants.PARTY_TYPES.COMPANY }, resetForm)
        setSelectedPartyId(null)
        resetForm()
      }}
    >
      {({
        handleSubmit,
        values,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        errors,
        touched,
        resetForm,
        submitCount,
        setValues,
      }) => {
        const generalErrors = getGeneralErrors(values)
        const fieldErrors = getFieldErrors(values)
        const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

        const formLoaderState = isSubmitting
          ? 'submitting'
          : (submitCount > 0 && !isEmpty(errors)) || hasApiErrors
          ? 'error'
          : undefined

        const handlePartySelect = (id: string): void => {
          resetForm()
          setValues(getPartyValues(id) as unknown as PartyEntityFormValues)
          setSelectedPartyId(id)
        }

        const handleRemoveAddress = (): void => {
          setFieldValue('address', emptyAddress)
          setShowAddress(false)
        }

        const isCurrentContactAnAgency = values.tags.includes('Agency')
        const shouldDisableFields = Boolean(selectedPartyId) || isCurrentContactAnAgency
        const isMissingRequiredFields = !hasAllRequiredFields(requiredFields, values)

        // Account number and account holder are mutually required
        const accountNumber = String(values.accountNumber || '').trim()
        const accountHolder = String(values.accountName || '').trim()
        const isAccountDetailsMissingWhenOneExists = Boolean(
          (accountNumber && !accountHolder) || (accountHolder && !accountNumber),
        )

        const buttonStyles = classNames([styles.footer], {
          [styles.disabled]: isMissingRequiredFields || isAccountDetailsMissingWhenOneExists,
        })

        return (
          <FormLoader
            onSubmit={handleSubmit}
            state={formLoaderState}
            buttonProps={{
              children: typeof getButtonText === 'function' ? getButtonText(values) : 'Save',
              disabled: isMissingRequiredFields || isAccountDetailsMissingWhenOneExists,
            }}
            ButtonContainer={ButtonContainer}
            ButtonContainerProps={{ className: buttonStyles }}
            persistErrorMessage={!hasApiErrors && !isEmpty(errors)}
            errorText={hasApiErrors ? 'Oops, something went wrong' : 'Resolve errors'}
            isDisabled={Boolean(isDisabled) || isCurrentContactAnAgency}
          >
            {isCurrentContactAnAgency && <InfoBox>Please contact support to update business details</InfoBox>}
            <div className={styles.body}>
              <FormErrors errors={generalErrors} />
              <>
                <EntityDetails
                  companiesOnly
                  requiredFields={requiredFields}
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  handleSelect={({ id }) => handlePartySelect(id)}
                  setFieldValue={setFieldValue}
                  errors={mergeRight(errors, fieldErrors)}
                  touched={touched}
                  allowPartySearch={allowSearchable}
                  submitCount={submitCount}
                  disableFields={shouldDisableFields}
                />
              </>

              <section>
                <Heading as="h2" size="a5" spacing="small" className={styles['heading-with-helper']}>
                  Address
                  <Tooltip
                    isOpen={isHovering}
                    body="This address will appear on invoices and statements generated for this contact"
                  >
                    {hoverableInfoIcon}
                  </Tooltip>
                </Heading>
                {showAddress ? (
                  <>
                    <AddressFormGroup
                      values={values?.address}
                      touched={touched}
                      errors={errors}
                      submitCount={submitCount}
                      disableFields={shouldDisableFields}
                      handleChange={handleChange}
                      handleBlur={handleBlur}
                    />
                    {!shouldDisableFields && !isEditingContact && (
                      <Button
                        ghost
                        size="xs"
                        className={styles['address-toggle--remove']}
                        onClick={handleRemoveAddress}
                      >
                        - Remove address
                      </Button>
                    )}
                  </>
                ) : (
                  <Button
                    ghost
                    size="xs"
                    className={styles['address-toggle--add']}
                    disabled={shouldDisableFields}
                    onClick={() => setShowAddress(true)}
                  >
                    + Add address
                  </Button>
                )}
              </section>

              <section className={styles['section-divider']}>
                <Heading as="h2" size="a5" spacing="small" className={styles.heading}>
                  Bank account
                </Heading>
                <BankDetails
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  setFieldValue={setFieldValue}
                  setFieldTouched={setFieldTouched}
                  errors={errors}
                  touched={touched}
                  accountDescription
                  disableFields={shouldDisableFields}
                />
              </section>
            </div>
          </FormLoader>
        )
      }}
    </Formik>
  )
}

export default PartyEntityForm
